import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Tabs,
  Alert,
  Space,
  Divider,
  Typography,
  Card,
  Select,
  message,
  Spin,
  Row,
  Col,
  Tag
} from 'antd';
import {
  CreditCardOutlined,
  DollarOutlined,
  WalletOutlined,
  SettingOutlined,
  LockOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { paymentService, PaymentSettings as PaymentSettingsType } from '../services/paymentService';
import { getApiBaseUrl, isDevelopmentMode } from '../utils/getBaseUrl';
import { copyToClipboard } from '../utils/clipboard';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface PaymentSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const PaymentSettingsModal: React.FC<PaymentSettingsModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PaymentSettingsType | null>(null);
  const [testMode, setTestMode] = useState(true);
  const [stripeStatus, setStripeStatus] = useState<'configured' | 'incomplete' | 'error'>('incomplete');
  const [affirmStatus, setAffirmStatus] = useState<'configured' | 'incomplete' | 'disabled'>('disabled');

  const isDevelopment = isDevelopmentMode();

  useEffect(() => {
    if (visible) {
      loadSettings();
    }
  }, [visible]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getSettings();
      setSettings(data);
      form.setFieldsValue(data);
      checkStatus(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = (data: PaymentSettingsType) => {
    // Check Stripe status
    // Note: stripe_secret_key and stripe_webhook_secret are not returned from API for security
    // We only check publishable key to determine if Stripe is configured
    if (data.stripe_enabled && data.stripe_publishable_key) {
      setStripeStatus('configured');
    } else if (data.stripe_publishable_key) {
      setStripeStatus('incomplete');
    } else {
      setStripeStatus('incomplete');
    }

    // Check Affirm status
    if (data.affirm_enabled && data.stripe_enabled) {
      setAffirmStatus('configured');
    } else if (data.stripe_enabled) {
      setAffirmStatus('disabled');
    } else {
      setAffirmStatus('disabled');
    }
  };

  const handleSave = async (values: any) => {
    try {
      setSaving(true);
      await paymentService.updateSettings(values);
      message.success('Payment settings saved successfully');
      loadSettings();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: 'configured' | 'incomplete' | 'error' | 'disabled') => {
    switch (status) {
      case 'configured':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'incomplete':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <WarningOutlined style={{ color: '#ff4d4f' }} />;
      case 'disabled':
        return <InfoCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusTag = (status: 'configured' | 'incomplete' | 'error' | 'disabled') => {
    switch (status) {
      case 'configured':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Configured</Tag>;
      case 'incomplete':
        return <Tag color="warning" icon={<WarningOutlined />}>Incomplete</Tag>;
      case 'error':
        return <Tag color="error" icon={<WarningOutlined />}>Error</Tag>;
      case 'disabled':
        return <Tag color="default">Disabled</Tag>;
    }
  };

  const stripeTabLabel = (
    <Space>
      <CreditCardOutlined />
      Stripe
      {getStatusIcon(stripeStatus)}
    </Space>
  );

  const affirmTabLabel = (
    <Space>
      <WalletOutlined />
      Affirm
      {getStatusIcon(affirmStatus)}
    </Space>
  );

  const manualTabLabel = (
    <Space>
      <DollarOutlined />
      Manual Payments
    </Space>
  );

  const settingsTabLabel = (
    <Space>
      <SettingOutlined />
      Payment Plans & Settings
    </Space>
  );

  if (loading) {
    return (
      <Modal
        title="Payment Settings"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={900}
        centered
      >
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Loading payment settings..." />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <Space>
          <CreditCardOutlined />
          Payment Processor Configuration
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
      >
        {/* Environment and Mode Indicator */}
        <Alert
          message={
            <Space>
              <strong>Current Environment:</strong>
              {isDevelopment ? (
                <Tag color="orange" icon={<WarningOutlined />}>Development Mode</Tag>
              ) : (
                <Tag color="green" icon={<CheckCircleOutlined />}>Production Mode</Tag>
              )}
            </Space>
          }
          description={
            isDevelopment
              ? "You're in development mode. Use Stripe test keys and test cards for testing."
              : "You're in production mode. Make sure to use LIVE Stripe keys for real transactions."
          }
          type={isDevelopment ? "warning" : "success"}
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Tabs
          defaultActiveKey="stripe"
          items={[
            {
              key: 'stripe',
              label: stripeTabLabel,
              children: (
                <div>
                  <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Space direction="vertical" size={0}>
                          <Text strong>Stripe Payment Processing</Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Accept credit and debit card payments securely
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        {getStatusTag(stripeStatus)}
                      </Col>
                    </Row>
                  </Card>

                  <Form.Item
                    label="Enable Stripe Payments"
                    name="stripe_enabled"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Alert
                    message={isDevelopment ? "Use Test Mode Keys" : "Use Live Mode Keys"}
                    description={
                      <div>
                        <p>
                          {isDevelopment
                            ? "Get test keys from your Stripe Dashboard in TEST MODE"
                            : "Get live keys from your Stripe Dashboard in LIVE MODE"
                          }
                        </p>
                        <p>
                          <strong>Dashboard:</strong>{' '}
                          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">
                            https://dashboard.stripe.com/apikeys
                          </a>
                        </p>
                        <p style={{ marginBottom: 0 }}>
                          {isDevelopment ? (
                            <Text type="warning">
                              Test keys start with <code>pk_test_</code> and <code>sk_test_</code>
                            </Text>
                          ) : (
                            <Text type="danger">
                              Live keys start with <code>pk_live_</code> and <code>sk_live_</code>
                            </Text>
                          )}
                        </p>
                      </div>
                    }
                    type={isDevelopment ? "info" : "warning"}
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <Form.Item
                    label="Stripe Publishable Key"
                    name="stripe_publishable_key"
                    extra={`${isDevelopment ? 'Test' : 'Live'} key starting with pk_${isDevelopment ? 'test' : 'live'}_...`}
                    rules={[
                      {
                        pattern: isDevelopment ? /^pk_test_/ : /^pk_live_/,
                        message: `Must be a ${isDevelopment ? 'test' : 'live'} publishable key (pk_${isDevelopment ? 'test' : 'live'}_...)`
                      }
                    ]}
                  >
                    <Input
                      placeholder={`pk_${isDevelopment ? 'test' : 'live'}_...`}
                      prefix={<LockOutlined />}
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Stripe Secret Key"
                    name="stripe_secret_key"
                    extra={`${isDevelopment ? 'Test' : 'Live'} key starting with sk_${isDevelopment ? 'test' : 'live'}_... (encrypted in database)`}
                    rules={[
                      {
                        pattern: isDevelopment ? /^sk_test_/ : /^sk_live_/,
                        message: `Must be a ${isDevelopment ? 'test' : 'live'} secret key (sk_${isDevelopment ? 'test' : 'live'}_...)`
                      }
                    ]}
                  >
                    <Input.Password
                      placeholder={`sk_${isDevelopment ? 'test' : 'live'}_...`}
                      prefix={<LockOutlined />}
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Stripe Webhook Secret (Optional but Recommended)"
                    name="stripe_webhook_secret"
                    extra={`For real-time payment updates. Starts with whsec_...`}
                  >
                    <Input.Password
                      placeholder="whsec_..."
                      prefix={<LockOutlined />}
                      size="large"
                    />
                  </Form.Item>

                  <Alert
                    message="Webhook Configuration"
                    description={
                      <div>
                        <p><strong>Webhook URL:</strong></p>
                        <Input
                          readOnly
                          value={`${getApiBaseUrl()}/api/payments/webhook/stripe`}
                          addonAfter={
                            <Button
                              type="link"
                              size="small"
                              onClick={async () => {
                                const success = await copyToClipboard(`${getApiBaseUrl()}/api/payments/webhook/stripe`);
                                if (success) {
                                  message.success('Webhook URL copied to clipboard');
                                } else {
                                  message.error('Failed to copy webhook URL');
                                }
                              }}
                            >
                              Copy
                            </Button>
                          }
                        />
                        <p style={{ marginTop: 8, marginBottom: 0 }}>
                          <small>
                            Configure this in Stripe Dashboard → Developers → Webhooks.
                            Listen for: <code>payment_intent.succeeded</code>, <code>payment_intent.payment_failed</code>, <code>charge.refunded</code>
                          </small>
                        </p>
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />

                  {isDevelopment && (
                    <Alert
                      message="Test Card Numbers"
                      description={
                        <div>
                          <p><strong>Successful Payment:</strong> <code>4242 4242 4242 4242</code></p>
                          <p><strong>Declined Payment:</strong> <code>4000 0000 0000 0002</code></p>
                          <p style={{ marginBottom: 0 }}>
                            <small>Use any future expiry date and any 3-digit CVC</small>
                          </p>
                        </div>
                      }
                      type="success"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </div>
              )
            },
            {
              key: 'affirm',
              label: affirmTabLabel,
              children: (
                <div>
                  <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Space direction="vertical" size={0}>
                          <Text strong>Affirm Buy Now, Pay Later</Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Offer 0-36 month financing with soft credit checks
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        {getStatusTag(affirmStatus)}
                      </Col>
                    </Row>
                  </Card>

                  {!settings?.stripe_enabled ? (
                    <Alert
                      message="Stripe Required"
                      description="Affirm requires Stripe to be enabled and configured first. Please configure Stripe in the previous tab."
                      type="warning"
                      showIcon
                    />
                  ) : (
                    <>
                      <Form.Item
                        label="Enable Affirm Financing"
                        name="affirm_enabled"
                        valuePropName="checked"
                        extra="Affirm must also be enabled in your Stripe Dashboard"
                      >
                        <Switch />
                      </Form.Item>

                      <Alert
                        message="About Affirm"
                        description={
                          <div>
                            <p><strong>Features:</strong></p>
                            <ul>
                              <li>✅ Soft credit check (won't affect customer's credit score)</li>
                              <li>✅ Instant approval decisions</li>
                              <li>✅ 0-36 month payment terms</li>
                              <li>✅ You get paid upfront (minus processing fees)</li>
                              <li>✅ Customer pays Affirm over time</li>
                              <li>✅ Minimum transaction: $50</li>
                            </ul>
                            <p style={{ marginBottom: 0 }}>
                              <strong>Setup:</strong> Enable Affirm in your{' '}
                              <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noopener noreferrer">
                                Stripe Dashboard → Settings → Payment methods
                              </a>
                            </p>
                          </div>
                        }
                        type="info"
                        showIcon
                      />
                    </>
                  )}
                </div>
              )
            },
            {
              key: 'manual',
              label: manualTabLabel,
              children: (
                <div>
                  <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                    <Space direction="vertical" size={0}>
                      <Text strong>Manual Payment Recording</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Record cash and check payments made in person
                      </Text>
                    </Space>
                  </Card>

                  <Form.Item
                    label="Accept Cash Payments"
                    name="accept_cash"
                    valuePropName="checked"
                    extra="Allow owners and advisors to record cash payments"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Accept Check Payments"
                    name="accept_check"
                    valuePropName="checked"
                    extra="Allow owners and advisors to record check payments"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Require Check Approval"
                    name="require_check_approval"
                    valuePropName="checked"
                    extra="If enabled, checks will need owner approval before being marked as completed"
                  >
                    <Switch />
                  </Form.Item>

                  <Alert
                    message="Manual Payment Recording"
                    description="Only users with Owner or Advisor roles can record manual payments. All manual payments are logged with timestamps and user information for audit purposes."
                    type="info"
                    showIcon
                  />
                </div>
              )
            },
            {
              key: 'settings',
              label: settingsTabLabel,
              children: (
                <div>
                  <Title level={5}>Payment Plan Settings</Title>

                  <Form.Item
                    label="Allow Payment Plans"
                    name="allow_payment_plans"
                    valuePropName="checked"
                    extra="Enable flexible payment plans for customers"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Minimum Payment Plan Amount"
                    name="min_payment_plan_amount"
                    extra="Minimum invoice amount required to offer payment plans"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix="$"
                      min={0}
                      precision={2}
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Maximum Payment Plan Duration"
                    name="max_payment_plan_months"
                    extra="Maximum number of months allowed for payment plans"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={24}
                      suffix="months"
                      size="large"
                    />
                  </Form.Item>

                  <Divider />

                  <Title level={5}>Down Payment Settings</Title>

                  <Form.Item
                    label="Require Down Payment"
                    name="require_down_payment"
                    valuePropName="checked"
                    extra="Require customers to make a down payment when creating a payment plan"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Minimum Down Payment Percentage"
                    name="min_down_payment_percentage"
                    extra="Required down payment as percentage of total (if enabled above)"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      suffix="%"
                      min={0}
                      max={100}
                      precision={1}
                      size="large"
                    />
                  </Form.Item>

                  <Divider />

                  <Title level={5}>Receipt & Notification Settings</Title>

                  <Form.Item
                    label="Send Payment Receipts"
                    name="send_payment_receipts"
                    valuePropName="checked"
                    extra="Automatically email receipts to customers after successful payments"
                  >
                    <Switch />
                  </Form.Item>

                  <Divider />

                  <Title level={5}>Late Fee Settings</Title>

                  <Form.Item
                    label="Charge Late Fees"
                    name="charge_late_fees"
                    valuePropName="checked"
                    extra="Automatically apply late fees to overdue payment plan installments"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Late Fee Amount"
                    name="late_fee_amount"
                    extra="Fixed late fee amount per overdue installment"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix="$"
                      min={0}
                      precision={2}
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Grace Period (Days)"
                    name="late_fee_grace_period_days"
                    extra="Number of days after due date before late fee is applied"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={30}
                      suffix="days"
                      size="large"
                    />
                  </Form.Item>
                </div>
              )
            }
          ]}
        />

        <Divider />

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={saving}
              icon={<CheckCircleOutlined />}
            >
              Save Payment Settings
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default PaymentSettingsModal;
